import { useQuery } from "@tanstack/react-query";

import { fetchMerchantSettings } from "checkout-query-client/api/fetch-merchant-settings";
import { BaseParams } from "checkout-query-client/hooks/types";
import { PaymentType } from "checkout-query-client/types/form-types";

import {
  Alipay,
  ApplePay,
  BillingAddressCollectionFields,
  CardPaymentMethod,
  EcpPaymentMethod,
  GetMerchantSettingsResponse,
  GetMerchantSettingsResponse_MerchantSettings,
  GetSettingsBundleResponse,
  GiftCardPaymentMethod,
  GooglePay,
  Ideal,
  IntegrationType,
  Layout,
  PayByBankPaymentMethod,
  PaymentButtonName,
  PaymentGatewayType,
  Paypal,
  Paze,
  PlainMessage,
  SepaPaymentMethod,
  Trustly,
  WechatPay,
} from "frontend-client";

export const useMerchantSettings = ({
  checkoutSessionToken,
  apiHost,
  version,
}: BaseParams) =>
  useQuery({
    queryKey: [
      {
        scope: "merchant-settings",
        apiHost,
        version,
        checkoutSessionToken,
      },
    ],
    queryFn: fetchMerchantSettings,
    select: transformMerchantSettings,
    enabled: !!checkoutSessionToken && !!apiHost,
  });

const isGetMerchantSettingsResponse = (
  res: GetSettingsBundleResponse | GetMerchantSettingsResponse
): res is GetMerchantSettingsResponse =>
  (res as GetMerchantSettingsResponse).merchantSettings !== undefined;

const transformMerchantSettings = (
  response: GetSettingsBundleResponse | GetMerchantSettingsResponse
): MerchantSettings | undefined => {
  if (isGetMerchantSettingsResponse(response)) {
    return response.merchantSettings;
  }
  const {
    uiSettings,
    paymentMethods,
    supportedShipToCountryRegion = [],
    billingAddressCountryRestriction = [],
    merchantDisplayName = "",
    integrationType = IntegrationType.HPP,
    layout = Layout.LAYOUT_DESIGN_UNSPECIFIED,
    ordersOnHpp,
    addressAutocompleteEnabled = true,
    paymentButtonName = PaymentButtonName.SUBMIT,
    orderMandates = [],
    paymentGatewayType,
    country,
  } = response.activeSettings ?? {};

  return {
    customTheme: uiSettings?.customTheme,
    formConfig: uiSettings?.formConfig,
    encryptionKey: "",
    encryptionKeyId: "",
    paymentSettings: [],
    safetechMerchantId: "",
    billingAddressCollectionFields:
      BillingAddressCollectionFields.BILLING_ZIP_ONLY,
    paymentMethods,
    supportedShipToCountryRegion,
    billingAddressCountryRestriction,
    merchantDisplayName,
    integrationType,
    layout,
    ordersOnHpp,
    addressAutocompleteEnabled,
    paymentButtonName,
    orderMandates,
    supportedPaymentGatewayType:
      paymentGatewayType ?? PaymentGatewayType.UNSPECIFIED,
    country: country ?? "",
  };
};

export type LpmPlainMessage = PlainMessage<
  Ideal | Alipay | Paypal | Trustly | WechatPay
>;

export type PaymentMethodData =
  | PlainMessage<CardPaymentMethod>
  | PlainMessage<EcpPaymentMethod>
  | PlainMessage<ApplePay>
  | PlainMessage<GooglePay>
  | PlainMessage<SepaPaymentMethod>
  | PlainMessage<Paze>
  | PlainMessage<PayByBankPaymentMethod>
  | PlainMessage<GiftCardPaymentMethod>
  | LpmPlainMessage;
export type PaymentMethod = {
  name: PaymentType;
  data: PaymentMethodData;
  script?: PaymentMethodScriptType;
  active?: boolean;
  isLoaded?: boolean;
  isError?: boolean;
  isLoading?: boolean;
};

export type PaymentMethodScriptType = {
  id: string;
  url: string;
  integrity?: string;
  crossOrigin?: boolean;
};

export type MerchantSettings =
  PlainMessage<GetMerchantSettingsResponse_MerchantSettings>;

/**
 * TODO: Keeping this for reference of writing tests later
 *
 * test("it should call dispatch setEnabledPaymentOptions", async () => {
    const dispatchMock = vi.fn();
    const merchantSettings = {
      paymentMethods: {
        ecpPaymentMethod: {
          enabled: false,
        },
        cardPaymentMethod: {
          enabled: true,
        },
        walletPaymentMethod: {},
      },
    };
    const payment: PaymentSlice = {
      paymentOption: "",
      paymentType: null,
      enabledPaymentOptions: [],
    };
    mockStore.mockImplementation((selector) => {
      const state: RootState = {
        dispatch: dispatchMock,
        merchantSettings,
        payment,
        checkoutSession: { paymentMethods: [], saveConsumerProfile: false },
      } as unknown as RootState;
      return selector ? selector(state) : state;
    });

    renderHook(() => useFormInit({ i18n: {} }));

    await waitFor(() => {
      expect(dispatchMock).toBeCalledTimes(3);
    });
    expect(dispatchMock.mock.calls).toEqual([
      [
        paymentSlice.actions.setEnabledPaymentOptions.create({
          enabledPaymentOptions: [PaymentType.creditCard],
        }),
      ],
      [
        paymentSlice.actions.setSelectedPayment.create({
          paymentType: PaymentType.creditCard,
          paymentOption: PaymentType.creditCard,
        }),
      ],
      [
        formsSlice.actions.initializeForm.create({
          formId: PaymentType.creditCard,
          merchantSettings,
          i18n: {},
          isValid: false,
          paymentType: PaymentType.creditCard,
          saveConsumerProfile: false,
          currencyCode: "USD",
        }),
      ],
    ]);
  });

  test("should call dispatch setEnabledPaymentOptions with MOPs for currency", async () => {
    const dispatchMock = vi.fn();
    const merchantSettings = {
      paymentMethods: {
        ecpPaymentMethod: {
          enabled: true,
          pointOfSaleCurrencyCodes: ["USD", "GBP"],
          confirmAccountNumber: true,
        },
        cardPaymentMethod: {
          enabled: true,
          securityCodeRequired: true,
          supportedCards: [
            {
              cardNetwork: CardNetwork.AMEX,
              pointOfSaleCurrencyCodes: ["EUR", "USD"],
            },
          ],
        },
        walletPaymentMethod: {},
      },
    };
    const payment: PaymentSlice = {
      paymentOption: "",
      paymentType: null,
      enabledPaymentOptions: [],
    };
    mockStore.mockImplementation((selector) => {
      const state: RootState = {
        dispatch: dispatchMock,
        merchantSettings,
        payment,
        checkoutSession: {
          paymentMethods: [],
          saveConsumerProfile: false,
          currencyCode: "EUR",
        },
      } as unknown as RootState;
      return selector ? selector(state) : state;
    });

    renderHook(() => useFormInit({ i18n: {} }));

    await waitFor(() => {
      expect(dispatchMock).toBeCalledTimes(3);
    });
    expect(dispatchMock.mock.calls).toEqual([
      [
        paymentSlice.actions.setEnabledPaymentOptions.create({
          enabledPaymentOptions: [PaymentType.creditCard],
        }),
      ],
      [
        paymentSlice.actions.setSelectedPayment.create({
          paymentType: PaymentType.creditCard,
          paymentOption: PaymentType.creditCard,
        }),
      ],
      [
        formsSlice.actions.initializeForm.create({
          formId: PaymentType.creditCard,
          merchantSettings,
          i18n: {},
          isValid: false,
          paymentType: PaymentType.creditCard,
          saveConsumerProfile: false,
          currencyCode: "EUR",
        }),
      ],
    ]);
  });
 */
